import React, { useState, useEffect } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Input, Modal, Spin, Upload, message, Row, Tag, notification, Select, Card, Statistic, Col  } from 'antd';
import { EditOutlined, DeleteOutlined, UserOutlined, StarOutlined, UploadOutlined, ReloadOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserGraduate, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import PasswordModal from './PasswordModal';
import EditStudentModal from './EditStudentModal';
import { jwtDecode } from "jwt-decode";
const { confirm } = Modal;
const { Option } = Select;

function EditDeleteStudent({ onCreateNewStudent }) {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [studentIdToDelete, setStudentIdToDelete] = useState(null);
  const [studentIdToEdit, setStudentIdToEdit] = useState(null);
  const [passwordError, setPasswordError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [activeCounts, setActiveCounts] = useState({ student: 0 });
  // Search input states (what user types)
  const [searchTerm, setSearchTerm] = useState('');
  const [nicknameTerm, setNicknameTerm] = useState('');
  const [phoneTerm, setPhoneTerm] = useState('');
  const [emailTerm, setEmailTerm] = useState('');
  const [selectedBranch, setSelectedBranch] = useState("all");
  const MAX_ACTIVE_USERS = {
    student: 1000,
  };
  // Actual search states (what gets sent to API)
  const [nameSearch, setNameSearch] = useState('');
  const [nicknameSearch, setNicknameSearch] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [emailSearch, setEmailSearch] = useState('');
  
  const [branches, setBranches] = useState([]);
  const rowsPerPage = 50;
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  let userRole = null;
  let userBranchId = null;

  if (token) {
    try {
      const decoded = jwtDecode(token);
      userRole = decoded.role;
      userBranchId = decoded.branch_id || decoded.branchId || null; 
    } catch (err) {
      console.error("Invalid token", err);
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Intl.DateTimeFormat(t('locale'), options).format(date).toUpperCase();
  };

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await axios.get(`${API_URL}/branch`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBranches(res.data);
      } catch (err) {
        console.error("Failed to fetch branches", err);
      }
    };

    if (userRole === "superadmin" || userRole === "admin") {
      fetchBranches();
    }
  }, [userRole, token]);

  const fetchStudents = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('token');

    const params = { page: currentPage, size: rowsPerPage };
    if (nameSearch.trim()) params.name = nameSearch.trim();
    if (nicknameSearch.trim()) params.nickname = nicknameSearch.trim();
    if (phoneSearch.trim()) params.phoneNumber = phoneSearch.trim();
    if (emailSearch.trim()) params.email = emailSearch.trim();
    if (userRole === "superadmin" && selectedBranch !== "all") {
      params.branch_id = selectedBranch;
    }

    const response = await axios.get(`${API_URL}/students`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    });

    const fetched = response.data.students.map(s => ({ ...s, key: s.userid }));
    setStudents(fetched);

    // 👇 count active students here
    const activeStudentCount = fetched.filter(
      (s) => s.status?.toLowerCase() === 'active'
    ).length;
    setActiveCounts({ student: activeStudentCount });

    setTotalItems(response.data.totalItems);
    setTotalPages(response.data.totalPages);
  } catch (error) {
    console.error('❌ Error fetching students:', error);
    setError(t('fetchStudentsError'));
  } finally {
    setLoading(false);
  }
};
  // Fetch students when component mounts or when actual search states change
  useEffect(() => {
    fetchStudents();
  }, [currentPage, nameSearch, nicknameSearch, phoneSearch, emailSearch, selectedBranch]);

  // Manual search function
  const handleSearch = () => {
    setNameSearch(searchTerm);
    setNicknameSearch(nicknameTerm);
    setPhoneSearch(phoneTerm);
    setEmailSearch(emailTerm);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Clear search function
  const handleClearSearch = () => {
    setSearchTerm('');
    setNicknameTerm('');
    setPhoneTerm('');
    setEmailTerm('');
    setNameSearch('');
    setNicknameSearch('');
    setPhoneSearch('');
    setEmailSearch('');
    setCurrentPage(1);
  };

  // Reset to page 1 when branch filter changes
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [selectedBranch]);

  

  const handleEdit = (studentId) => {
    setStudentIdToEdit(studentId);
    setIsEditModalOpen(true);
  };

  const handleDelete = (studentId) => {
    setStudentIdToDelete(studentId);
    setPasswordError('');
    setIsPasswordModalOpen(true);
  };

  const confirmDelete = async (password) => {
    setIsPasswordModalOpen(false);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('noTokenFound'));
        return;
      }
      await axios.delete(`${API_URL}/students/${studentIdToDelete}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { password },
      });

      // Refresh the list after deletion
      fetchStudents();
      Modal.success({ title: t('success'), content: t('studentDeleted') });
    } catch (error) {
      console.error('Error deleting student:', error);
      if (error.response && error.response.status === 401) {
        setPasswordError(t('invalidPassword'));
        setIsPasswordModalOpen(true);
      } else {
        setError(t('deleteStudentError'));
      }
    }
  };

  const saveStudent = async (updatedStudent) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/students/${updatedStudent.userid}`, updatedStudent, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Refresh the list after update
      fetchStudents();
      setIsEditModalOpen(false);
      Modal.success({ title: t('success'), content: t('studentUpdated') });
    } catch (error) {
      console.error('Error updating student:', error);
      setError(t('updateStudentError'));
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return '-';
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const updateUserStatus = (userId, status, role = 'student') => {
  if (status === 'active' && activeCounts[role] >= MAX_ACTIVE_USERS[role]) {
    notification.warning({
      message: t('alert'),
      description: t('maxLimitReached', {
        role: t(role),
        limit: MAX_ACTIVE_USERS[role],
      }),
    });
    return;
  }

  confirm({
    title: t('confirmStatusChange'),
    icon: <ExclamationCircleOutlined />,
    onOk: async () => {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API_URL}/users/${userId}/status`,
          { status },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchStudents();
      } catch (error) {
        console.error('Error updating user status:', error);
      }
    },
  });
};

  const handleNationalIdUpload = async (studentId) => {
    let fileToUpload = null;
  
    Modal.confirm({
      title: t('Upload National ID'),
      content: (
        <Upload
          beforeUpload={(file) => {
            const isValidType = file.type === 'application/pdf' || 
                                file.type === 'image/jpeg' || 
                                file.type === 'image/jpg' || 
                                file.type === 'image/png';
            if (!isValidType) {
              message.error(t('onlyPdfAndImagesAllowed'));
              return Upload.LIST_IGNORE;
            }
            
            fileToUpload = file;
            return false;
          }}
          maxCount={1}
          accept=".pdf,.jpg,.jpeg,.png"
        >
          <Button icon={<UploadOutlined />}>{t('Select File')}</Button>
        </Upload>
      ),
      onOk: async () => {
        if (!fileToUpload) {
          message.error(t('Please Select File'));
          return;
        }
  
        try {
          const token = localStorage.getItem('token');
          const formData = new FormData();
          formData.append('nationalIdFile', fileToUpload);
          formData.append('userId', studentId);
      
          await axios.post(`${API_URL}/upload/national_ids/upload`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            }
          });
  
          message.success(t('National ID Uploaded Successfully'));
          
          // Refresh the student list
          fetchStudents();
        } catch (error) {
          console.error('Error uploading national ID:', error);
          message.error(t('Upload Failed.'));
        }
      },
      onCancel: () => {}
    });
  };

  const branchColors = {
    1: "blue",
    2: "green",
    3: "magenta", 
  };
  
  const nationalIdColumn = {
    title: t('nationalId'),
    dataIndex: 'national_id_pdf',
    render: (pdf, student) => (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {pdf ? (
          <>
            <a 
              href={`${API_URL}/upload/national_ids/${pdf.split('/').pop()}`} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ marginRight: '8px' }}
            >
              <FontAwesomeIcon icon={faFilePdf} style={{ color: 'red' }} />
            </a>
            
            <Button 
              size="small" 
              type="text" 
              icon={<UploadOutlined />} 
              onClick={(e) => {
                e.stopPropagation();
                handleNationalIdUpload(student.userid);
              }}
              title={t('replaceFile')}
            />
          </>
        ) : (
          <Button 
            size="small" 
            icon={<UploadOutlined />} 
            onClick={(e) => {
              e.stopPropagation();
              handleNationalIdUpload(student.userid);
            }}
          >
          </Button>
        )}
      </div>
    ),
  };

  const viewStudentDashboard = (studentId) => {
    navigate(`/student-dashboard-admin/${studentId}`);
  };

  const viewStudentPerformance = (studentId) => {
    navigate(`/studentperformance/${studentId}`);
  };

  const columns = [
    {
      title: '',
      dataIndex: 'dashboard',
      render: (_, student) => (
        <Button shape="circle"
                icon={<UserOutlined />} onClick={() => viewStudentDashboard(student.userid)} />
      ),
    },
    {
      title: '',
      dataIndex: 'performance',
      render: (_, student) => (
        <Button shape="circle"
        icon={<StarOutlined />} onClick={() => viewStudentPerformance(student.userid)} />
      ),
    },
    {
      title: t('branch'),
      dataIndex: 'branch_id',
      render: (branchId) => {
        const branch = branches.find((b) => b.id === branchId);
        const color = branchColors[branchId] || "default";
        return branch ? (
          <Tag color={color}>{branch.name}</Tag>
        ) : (
          <Tag>-</Tag>
        );
      }
    },
    { 
      title: t('name'),
      render: (_, student) => `${student.firstname || ''} ${student.lastname || ''}` 
    },
    { title: t('nickname'), dataIndex: 'nickname' },
    { title: t('dob'), dataIndex: 'date_of_birth', render: formatDate },
    { title: t('age'), dataIndex: 'date_of_birth', render: calculateAge },
    { title: t('email'), dataIndex: 'email' },
    { title: t('phone'), dataIndex: 'phone' },
    { title: t('parent'), dataIndex: 'guardiancontact' },
    { title: t('school'), dataIndex: 'schoolname' },

{
  title: t('status'),
  dataIndex: 'status',
  key: 'status',
  render: (status, student) => {
    const isActive = status?.toLowerCase() === 'active';

    return (
      <Select
        value={isActive ? 'active' : 'inactive'}
        onChange={(value) => updateUserStatus(student.userid, value, 'student')}
        className={status === 'active' ? 'select-active' : 'select-inactive'}
        dropdownMatchSelectWidth={false}
      >
        <Option value="active">
          {t('Active')}
        </Option>
        <Option value="inactive">
          {t('Inactive')}
        </Option>
      </Select>
    );
  },
},
    nationalIdColumn,
    {
      title: t('actions'),
      dataIndex: 'actions',
      render: (_, student) => (
        <div>
          <Button type="primary"
              shape="circle" 
              icon={<EditOutlined />} 
              style={{ marginRight: '5px' }}
              onClick={() => handleEdit(student.userid)} />
          <Button shape="circle" 
          type="primary"
              icon={<DeleteOutlined />}
               onClick={() => handleDelete(student.userid)} />
        </div>
      ),
    }, 
  ];

  if (loading) return <Spin tip={t('loading')} />;
  if (error) return <p>{error}</p>;

  return (
    <div>
<Row gutter={16} style={{ marginBottom: 16 }}>
  <Col xs={24} sm={6}>
    <Card>
      <Statistic
        title={t('Total Students')}
        value={totalItems}
      />
    </Card>
  </Col>

  <Col xs={24} sm={6}>
    <Card>
      <Statistic
        title={t('Active Students')}
        value={activeCounts.student}
        valueStyle={{ color: '#16a34a' }}
      />
    </Card>
  </Col>

  <Col xs={24} sm={6}>
    <Card>
      <Statistic
        title={t('Inactive Students')}
        value={totalItems - activeCounts.student}
        valueStyle={{ color: '#dc2626' }}
      />
    </Card>
  </Col>

  <Col xs={24} sm={6}>
    <Card>
      <Statistic
        title={t('Max Active Allowed')}
        value={MAX_ACTIVE_USERS.student}
        valueStyle={{ color: '#3b82f6' }} // blue
      />
    </Card>
  </Col>
</Row>

      
      <Button type="primary" style={{ marginRight: 16 }} icon={<FontAwesomeIcon icon={faUserGraduate} />} onClick={onCreateNewStudent}>
        {t('createNewStudent')}
      </Button>
      <Button 
        icon={<ReloadOutlined />} 
        onClick={fetchStudents}
      >
        {t('refresh')}
      </Button>
      
      <div style={{ marginTop: 16, marginBottom: 16 }}>
        <Row>
          {userRole === "superadmin" && (
            <div style={{ marginRight: 16 }}>
              <select
                value={selectedBranch}
                onChange={(e) => setSelectedBranch(e.target.value)}
                style={{ padding: 6, minWidth: 200 }}
              >
                <option value="all">{t("All Branches")}</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </div>
          )}
   
          <Input
            placeholder={t('searchByName')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onPressEnter={handleSearch}
            style={{ marginBottom: 8, width: 200, marginRight: 16 }}
          />
          <Input
            placeholder={t('Search by Nickname')}
            value={nicknameTerm}
            onChange={(e) => setNicknameTerm(e.target.value)}
            onPressEnter={handleSearch}
            style={{ marginBottom: 8, width: 200, marginRight: 16 }}
          />
          <Input
            placeholder={t('searchByPhone')}
            value={phoneTerm}
            onChange={(e) => setPhoneTerm(e.target.value)}
            onPressEnter={handleSearch}
            style={{ marginBottom: 8, width: 200, marginRight: 16 }}
          />
          <Input
            placeholder={t('searchByEmail')}
            value={emailTerm}
            onChange={(e) => setEmailTerm(e.target.value)}
            onPressEnter={handleSearch}
            style={{ marginBottom: 8, width: 200, marginRight: 16 }}
          />
          <Button type="primary" onClick={handleSearch} style={{ marginRight: 8 }}>
            {t('Search')}
          </Button>
          <Button onClick={handleClearSearch}>
            {t('Clear')}
          </Button>
        </Row>
      </div>
      
      <Table
        columns={columns}
        dataSource={students}
        rowKey="userid"
        pagination={{
          current: currentPage,
          pageSize: rowsPerPage,
          total: totalItems,
          showSizeChanger: false,
          onChange: (page) => setCurrentPage(page),
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} students`,
        }}
        scroll={{ x: 'max-content' }}
      />
      
      <PasswordModal
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
        onConfirm={confirmDelete}
        errorMessage={passwordError}
      />
      <EditStudentModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={saveStudent}
        studentId={studentIdToEdit}
      />
    </div>
  );
}

export default withRoleAccess(EditDeleteStudent, ['admin', 'superadmin']);